import { css } from '@emotion/core';
import ColorInput from 'components/color-input';
import ColorPalette from 'components/color-palette';
import React, { useCallback, useRef, useState } from 'react';
import { previewLecturePageViewOptions } from 'redux/actions/lecture-pages';
import { useAppDispatch } from 'redux/store';
import NvPopover from 'shared/components/nv-popover';
import useClickOutside from 'shared/hooks/use-click-outside';
import useUserColors from 'shared/hooks/use-user-colors';
import { colorRegex, gray5 } from 'styles/global_defaults/colors';
import { LecturePageEditContentParams } from '../lecture-page-content';

const popoverStyles = css`
  .bs4-popover-body {
    padding: 0;
  }

  .arrow {
    display: none;
  }
`;

export const LectureHeaderColorPicker = (props: {
  /** A callback used to signal this color picker should be closed. Used to return the LecturecontentEditHeaderButton
   * component back to showing the Edit Header button UX */
  closeColorPicker: () => void,
} & LecturePageEditContentParams) => {
  const dispatch = useAppDispatch();
  const isDirtyRef = React.useRef(false);
  const [colorDraft, setColorDraft] = useState(() => {
    const initialColor = props.lecturePage.viewOptions.backgroundColor;
    if (!initialColor) {
      return '#ffffff';
    }

    return initialColor.startsWith('#') ? initialColor : `#${initialColor}`;
  });
  const [showPopover, setShowPopover] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const colorPaletteRef = useRef<HTMLDivElement>(null);
  const { updateRecentlyUsedColorsIfNeeded } = useUserColors({ currentLecture: props.lecturePage });

  const onColorSelected = (newColor: string) => {
    setColorDraft(newColor);
    isDirtyRef.current = true;
    dispatch(previewLecturePageViewOptions({
      lecturePageId: props.lecturePageId,
      viewOptions: {
        backgroundColor: newColor,
      },
    }));
  };

  const saveSelectedColor = () => {
    props.saveLecturePageData({
      // Reset the cover image whenever saving a background color since these two modes are mutually exclusive
      coverImageUrl: null,
      viewOptions: {
        ...props.lecturePage.viewOptions,
        backgroundColor: colorDraft,
      },
    });
  };

  const finishEditionCallback = () => {
    setShowPopover(false);
    saveSelectedColor();
    updateRecentlyUsedColors(colorDraft);
    props.closeColorPicker();
  };

  const finishEditionCallbackRef = React.useRef<Function>();
  finishEditionCallbackRef.current = finishEditionCallback;

  // Closes this entire color picker
  useClickOutside(ref, (e) => {
    // Avoid closing the color palette popover & hiding the color picker if you're clicking inside the color palette itself
    if (colorPaletteRef?.current && !colorPaletteRef.current.contains(e.target as any)) {
      finishEditionCallback();
    }
  }, [colorDraft]);

  React.useEffect(() => () => {
    if (isDirtyRef.current) {
      finishEditionCallbackRef.current();
    }
  }, []);

  const updateRecentlyUsedColors = useCallback((newColor: string) => {
    // Taken from getColorInputProps in ColorPickerPoper. We should probably refactor this elsewhere
    if (newColor.match(colorRegex)) {
      updateRecentlyUsedColorsIfNeeded(newColor, true);
    }
  }, [updateRecentlyUsedColorsIfNeeded]);

  const togglePopover = (setPopoverOpen: boolean) => {
    if (!setPopoverOpen) {
      updateRecentlyUsedColors(colorDraft);
    }
    setShowPopover(setPopoverOpen);
  };

  return (
    <div ref={ref}>
      <NvPopover
        content={(
          <div ref={colorPaletteRef}>
            <ColorPalette
              activeColor={colorDraft}
              currentLecture={props.lecturePage}
              onChange={(val) => {
                onColorSelected(val);
              }}
              preserveColorOrder
            />
          </div>
        )}
        show={showPopover}
        placement='bottom-end'
        overlayStyles={popoverStyles}
        offset={0}
      >
        <ColorInput
          name='lesson-header-color-input'
          onChange={(e) => {
            onColorSelected(e.target.value);
          }}
          value={colorDraft}
          onValueChange={(e) => {
            updateRecentlyUsedColors(colorDraft);
            togglePopover(false);
          }}
          onFocus={() => setShowPopover(true)}
          // Inverting showPopover makes the color picker button toggle the popover on/off instead of just showing
          showPalette={() => {
            togglePopover(!showPopover);
          }}
        />
      </NvPopover>
    </div>
  );
};

export default LectureHeaderColorPicker;
